import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

Font.register({
  family: "Manrope",
  fonts: [
    { src: "/fonts/Manrope-Regular.ttf", fontWeight: "normal" },
    { src: "/fonts/Manrope-Bold.ttf", fontWeight: "bold" },
    { src: "/fonts/Manrope-Medium.ttf", fontWeight: "medium" },
  ],
});

// ✅ All columns defined in one place
const COL = {
  empId: 0.8,
  name: 1.6,
  jobTitle: 1.2,
  department: 1.0,
  division: 1.2,
  date: 1.2,
  inTime: 0.8,
  outTime: 0.8,
  normalHrs: 0.7,
  workedHrs: 0.7,
  otHrs: 0.5,
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    paddingBottom: 110,
    fontSize: 10,
    fontFamily: "Manrope",
  },
  headerSpace: {
    height: 80,
    marginBottom: 10,
  },
  header_image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  centerDiv: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 10,
  },
  mainTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 3,
  },
  smallText: {
    fontSize: 9,
    color: "grey",
    marginBottom: 6,
  },

  // ── Table ──────────────────────────────────
  tableHeader: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 6,
    backgroundColor: "#2663EB",
  },
  tableHeaderCell: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#ffffff",
  },

  // Outer row per employee
  employeeRow: {
    flexDirection: "row",
    marginVertical:"4px",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    alignItems: "flex-start",
  },

  // Common fields (left side)
  commonFields: {
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingVertical: 4,
    paddingHorizontal: 6,
    // borderRightWidth: 1,
    borderRightColor: "#eee",
  },
  cell: {
    fontSize: 8,
    paddingVertical: 1,
  },
  cellBold: {
    fontSize: 8,
    fontWeight: "bold",
    paddingVertical: 1,
  },

  // Punch rows (right side)
  punchesColumn: {
    flex: 1,
    flexDirection: "column",
  },
  punchRow: {
    flexDirection: "row",
    // borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  punchCell: {
    fontSize: 8,
  },

  // ── Footer ─────────────────────────────────
  footerSpace: {
    position: "absolute",
    bottom: 22,
    left: 0,
    right: 0,
    height: 80,
  },
  footer_image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  footerPageNum: {
    position: "absolute",
    bottom: 6,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 9,
    color: "grey",
  },
});

const LeaveLedgerDocument = ({ leaveLedgerPrintData }) => {
  const bondTimeportNoData = JSON.parse(
    localStorage.getItem("BondTimeportNoData") || "[]",
  );

  const headerImage = bondTimeportNoData[0]?.REP_HEADER;
  const footerImage = bondTimeportNoData[0]?.REP_FOOTER;

  // ✅ Group by EMP ID — common fields once, punches array for repeated
  const groupedRows = Object.values(
    (leaveLedgerPrintData || []).reduce((acc, r) => {
      const key = r["EMP ID"];
      if (!acc[key]) {
        acc[key] = { ...r, punches: [] };
      }
      acc[key].punches.push({
        DATE: r.DATE,
        IN_TIME: r.IN_TIME,
        OUT_TIME: r.OUT_TIME,
        NormalHrs: r.NormalHrs,
        WORKED_HOURS: r.WORKED_HOURS,
        OT_HOURS: r.OT_HOURS,
      });
      return acc;
    }, {}),
  );

  const fmt = (val, format) => {
    const d = dayjs(val);
    return d.isValid() ? d.format(format) : "-";
  };

  // Common flex total for left side
  const commonFlex =
    COL.empId + COL.name + COL.jobTitle + COL.department + COL.division;

  // Punch flex total for right side
  const punchFlex =
    COL.date +
    COL.inTime +
    COL.outTime +
    COL.normalHrs +
    COL.workedHrs +
    COL.otHrs;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER IMAGE */}
        <View style={styles.headerSpace} fixed>
          {headerImage && (
            <Image src={headerImage} style={styles.header_image} />
          )}
        </View>

        {/* TITLE */}
        <View style={styles.centerDiv}>
          <Text style={styles.mainTitle}>Leave Ledger Report</Text>
          <Text style={styles.smallText}>
            Total: {leaveLedgerPrintData?.length || 0} records
          </Text>
        </View>

        {/* TABLE HEADER */}
        <View style={styles.tableHeader}>
          <View style={{ flex: 0.6 }}>
            <Text style={styles.tableHeaderCell}>Sl No</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.tableHeaderCell}>Leave Type</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.tableHeaderCell}>From Date</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.tableHeaderCell}>To Date</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.tableHeaderCell}>Rejoin On</Text>
          </View>
          <View style={{ flex: 0.8 }}>
            <Text style={styles.tableHeaderCell}>Leaves</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.tableHeaderCell}>Status</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.tableHeaderCell}>Approved By</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.tableHeaderCell}>Approved Date</Text>
          </View>
        </View>

        {/* TABLE ROWS */}
        {leaveLedgerPrintData?.map((r, i) => (
          <View key={i} style={styles.employeeRow}>
            <View style={{ flex: 0.6 }}>
              <Text style={styles.cell}>{i + 1}</Text>
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.cell}>
                {r?.Leave_Type?.replace("X", "") || "-"}
              </Text>
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.cell}>{fmt(r?.FROM_DT, "YYYY-MM-DD")}</Text>
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.cell}>{fmt(r?.TO_DATE, "YYYY-MM-DD")}</Text>
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.cell}>{fmt(r?.REJOIN_ON, "YYYY-MM-DD")}</Text>
            </View>

            <View style={{ flex: 0.8 }}>
              <Text style={styles.cell}>{r?.LEAVES ?? 0}</Text>
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.cell}>{r?.STATUS || "-"}</Text>
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.cell}>{r?.APRD_BY || "-"}</Text>
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.cell}>{fmt(r?.APRV_DT, "YYYY-MM-DD")}</Text>
            </View>
          </View>
        ))}

        {/* FOOTER IMAGE */}
        <View style={styles.footerSpace} fixed>
          {footerImage && (
            <Image src={footerImage} style={styles.footer_image} />
          )}
        </View>

        {/* PAGE NUMBER */}
        <Text
          style={styles.footerPageNum}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

export default LeaveLedgerDocument;
